import { useState } from "react";
import { cn } from "@/lib/utils";

interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  className?: string;
  priority?: boolean;
}

/**
 * Rewrites framerusercontent or Supabase Storage URLs to a resized variant.
 * Other URLs pass through unchanged.
 */
/** Framer only honours a fixed set of scale-down-to buckets. */
const FRAMER_BUCKETS = [512, 1024, 2048, 4096];

function optimizedSrc(src: string, width?: number): string {
  if (!width) return src;
  try {
    const url = new URL(src);
    if (url.hostname.includes("framerusercontent.com")) {
      const bucket = FRAMER_BUCKETS.find((b) => b >= width) ?? FRAMER_BUCKETS[FRAMER_BUCKETS.length - 1];
      url.searchParams.delete("w");
      url.searchParams.set("scale-down-to", String(bucket));
      return url.toString();
    }
    // Supabase Storage public object → image transformation endpoint
    if (url.hostname.endsWith(".supabase.co") && url.pathname.includes("/storage/v1/object/public/")) {
      url.pathname = url.pathname.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/");
      url.searchParams.set("width", String(width));
      url.searchParams.set("quality", "72");
      return url.toString();
    }
  } catch {
    // not a valid URL, return as-is
  }
  return src;
}

/**
 * Generates a srcSet string for responsive images.
 */
function buildSrcSet(src: string, baseWidth: number): string | undefined {
  try {
    const url = new URL(src);
    const isTransformable =
      url.hostname.includes("framerusercontent.com") ||
      (url.hostname.endsWith(".supabase.co") && url.pathname.includes("/storage/v1/object/public/"));
    if (!isTransformable) return undefined;
  } catch {
    return undefined;
  }

  const sizes = [
    Math.round(baseWidth * 0.5),
    Math.round(baseWidth * 0.75),
    baseWidth,
    Math.round(baseWidth * 1.5),
  ];
  const seen = new Set<string>();
  const entries: string[] = [];
  for (const w of sizes) {
    const candidate = optimizedSrc(src, w);
    if (seen.has(candidate)) continue;
    seen.add(candidate);
    entries.push(`${candidate} ${w}w`);
  }
  return entries.join(", ");
}

const OptimizedImage = ({
  src,
  alt,
  width,
  className,
  loading = "lazy",
  priority = false,
  ...props
}: OptimizedImageProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const finalSrc = optimizedSrc(src, width);
  const srcSet = width ? buildSrcSet(src, width) : undefined;

  if (hasError) {
    return (
      <div className="w-full h-full bg-muted flex items-center justify-center">
        <span className="text-muted-foreground text-xs font-body">Image à venir</span>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      {isLoading && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}
      <img
        src={finalSrc}
        srcSet={srcSet}
        sizes={srcSet ? `(max-width: ${width}px) 100vw, ${width}px` : undefined}
        alt={alt}
        loading={priority ? "eager" : loading}
        fetchPriority={priority ? "high" : "auto"}
        decoding="async"
        className={cn(
          "w-full h-full object-cover transition-opacity duration-500",
          isLoading ? "opacity-0" : "opacity-100",
          className
        )}
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setIsLoading(false);
          setHasError(true);
        }}
        {...props}
      />
    </div>
  );
};

export { optimizedSrc, buildSrcSet };
export default OptimizedImage;
